import * as React from 'react'
import { closeArticleTagModal } from '../../store/modals/actions'
import ModalHeader from './Parts/ModalHeader'
import { AppState } from '../../store'
import { AnyAction, Dispatch } from 'redux'
import { connect } from 'react-redux'
import ModalWindow from './Parts/ModalWindow'
import ModalMiddle from './Parts/ModalMiddle'
import ModalContent from './Parts/ModalContent'
import { useTranslation } from 'react-i18next'
import ModalLoader from './Parts/ModalLoader'
import Button from '../Button/Button'
import { CurrentUser, DisplayableError, ArticleTag } from '../../types'
import TooltipError from '../Tooltips/ErrorTooltip'
import { ArticleTagsController } from '../../controllers'

interface IStateToProps {
  currentUser: CurrentUser
  helpCenterId: string
  articleTag?: ArticleTag
  onSubmit: (articleTag: ArticleTag) => void
  onDelete: (articleTag: ArticleTag) => void
}

interface IDispatchToProps {
  close: typeof closeArticleTagModal
}

type IProps = IDispatchToProps & IStateToProps

interface IState {
  didInitialLoad: boolean
  articleTag: ArticleTag
  errors: DisplayableError[]
}

const ArticleTagModal = (props: IProps) => {
  const { t } = useTranslation()
  const { currentUser, helpCenterId, articleTag: propsArticleTag } = props
  const { workspace: { setting } } = currentUser
  const [state, setState] = React.useState<IState>({
    didInitialLoad: false,
    articleTag: null,
    errors: [],
  })
  const { didInitialLoad, articleTag, errors } = state

  React.useEffect(() => {
    getForm().catch(console.error)
  }, [])

  const getForm = async () => {
    try {
      const { article_tag } = await ArticleTagsController.getForm(helpCenterId, propsArticleTag.id)

      setState({
        ...state,
        articleTag: {
          ...article_tag,
          ...propsArticleTag,
        },
        didInitialLoad: true,
      })
    } catch (ex) {
      console.error(ex)
    }
  }

  const onNameChange = (e) => {
    const newName = e.currentTarget.value

    setState({ ...state, articleTag: { ...articleTag, name: newName } })
  }

  const onCloseClick = () => {
    props.close()
  }

  const onDeleteClick = async () => {
    try {
      await ArticleTagsController.delete(helpCenterId, articleTag.id)
      props.onDelete(articleTag)
      onCloseClick()
    } catch (ex) {
      console.error(ex)
    }
  }

  const onFormSubmit = async (e) => {
    e.preventDefault()

    try {
      let response = null
      if (articleTag.id) {
        response = await ArticleTagsController.update(helpCenterId, articleTag)
      } else {
        response = await ArticleTagsController.create(helpCenterId, articleTag)
      }

      if (response.errors) {
        setState({ ...state, errors: response.errors })
      } else {
        props.onSubmit(response)
        props.close()
      }

    } catch (ex) {
      console.error(ex)
    }
  }

  const onErrorsDismiss = () => {
    setState({ ...state, errors: [] })
  }

  return (
    <ModalWindow>
      <ModalHeader
        title={articleTag?.id ? t('ArticleTagModal::Update tag') : t('ArticleTagModal::Create tag')}
        onCloseClick={onCloseClick}
      />

      {!didInitialLoad && <ModalLoader />}
      {didInitialLoad && <ModalMiddle>
        <ModalContent>
          <form onSubmit={onFormSubmit}>
            <div className='grid'>
              <div className='grid-cell with-12col'>
                <div className='form-item'>
                  <label>{t('ArticleTagModal::Name')} <span>*</span></label>
                  <input
                    type='text'
                    placeholder={t('ArticleTagModal::Name')}
                    onChange={onNameChange}
                    value={articleTag.name}
                  />
                </div>
              </div>
            </div>

            <input type='submit' style={{ display: 'none' }} onClick={onFormSubmit} />
          </form>
        </ModalContent>

        <div className='modal-footer'>
          <div>
            {articleTag?.id && <Button
              type='danger'
              text={t('ArticleTagModal::Delete')}
              onClick={onDeleteClick}
            />}
          </div>
          <div key='main-action' className='popover-wrapper'>
            <TooltipError
              errors={errors}
              onDismiss={onErrorsDismiss}
            />
            <Button
              type='success'
              text={t('ArticleTagModal::Save')}
              onClick={onFormSubmit}
            />
          </div>
        </div>
      </ModalMiddle>}
    </ModalWindow >
  )
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser
    },
    modals: {
      articleTagModal: {
        helpCenterId,
        articleTag,
        onSubmit,
        onDelete,
      }
    }
  } = state

  return {
    currentUser: currentUser,
    helpCenterId: helpCenterId,
    articleTag: articleTag,
    onSubmit: onSubmit,
    onDelete: onDelete
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    close: () => dispatch(closeArticleTagModal()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ArticleTagModal)